import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAzureAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loginToBackend = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (!isAzureAuthenticated || accounts.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });

      // Use idToken instead of accessToken - idToken has the app's clientId as audience
      const result = await authApi.login(response.idToken);
      setUser(result.user);
    } catch (error) {
      console.error('Backend login failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, isAzureAuthenticated, inProgress]);

  useEffect(() => {
    loginToBackend();
  }, [loginToBackend]);

  const login = async () => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      if (error.errorCode !== 'interaction_in_progress') {
        console.error('Login failed:', error);
      }
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      setUser(null);
      await instance.logoutPopup();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const refreshUser = async () => {
    try {
      const result = await authApi.getMe();
      setUser(result.user);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperAdmin: user?.role === 'superadmin',
    login,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
